import { z } from 'zod';
import { ClientMessage, ServerMessage } from './messages.js';
import { normalizeRoomCode, isValidRoomCode, sanitizeDisplayText } from './sanitize.js';
// =============================================================================
// HTTP API Request Schemas
// =============================================================================
/**
 * Room code path parameter schema
 */
export const RoomCodeParam = z.object({
    code: z
        .string()
        .min(1, 'Room code is required')
        .max(10, 'Room code too long')
        .transform((code) => normalizeRoomCode(code))
        .refine(isValidRoomCode, 'Invalid room code format'),
});
/**
 * Client log entry schema
 */
export const ClientLogEntry = z.object({
    level: z.enum(['trace', 'debug', 'info', 'warn', 'error']),
    message: z
        .string()
        .max(1000, 'Log message too long')
        .transform((msg) => sanitizeDisplayText(msg, 1000)),
    context: z.record(z.unknown()).optional(),
});
/**
 * Client logs batch request schema
 */
export const ClientLogsBatch = z.object({
    logs: z.array(ClientLogEntry).max(50, 'Too many logs in batch').default([]),
});
// =============================================================================
// Validation Helper Functions
// =============================================================================
/**
 * Parse and validate a client message
 */
export function parseClientMessage(data) {
    try {
        const message = ClientMessage.parse(data);
        return { success: true, message };
    }
    catch (err) {
        if (err instanceof z.ZodError) {
            return { success: false, error: err.errors[0]?.message ?? 'Invalid message' };
        }
        return { success: false, error: 'Failed to parse message' };
    }
}
/**
 * Parse and validate a server message
 */
export function parseServerMessage(data) {
    try {
        const message = ServerMessage.parse(data);
        return { success: true, message };
    }
    catch (err) {
        if (err instanceof z.ZodError) {
            return { success: false, error: err.errors[0]?.message ?? 'Invalid message' };
        }
        return { success: false, error: 'Failed to parse message' };
    }
}
/**
 * Safe JSON parse
 */
export function safeJsonParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=validation.js.map